import type { FC } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Radar } from 'lucide-react'
import { apiFetch } from '@/lib/api-client'

interface StatusResponse {
    collecting: boolean
    openJobs: number
    lastFinishedAt: string | null
}

/**
 * Shown while the board collects its first batch of jobs. Polls the status and
 * reloads the page data as soon as jobs arrive.
 */
export const CollectingState: FC = () => {
    const router = useRouter()

    const { data } = useQuery({
        queryKey: ['jobs-status'],
        queryFn: () => apiFetch<StatusResponse>('/api/jobs/status'),
        refetchInterval: 4000
    })

    const ready = Boolean(data && (data.openJobs > 0 || !data.collecting))

    useEffect(() => {
        if (ready) void router.replace(router.asPath, undefined, { scroll: false })
    }, [ready, router])

    return (
        <div role='status' className='flex flex-col items-center gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-6 py-14 text-center'>
            <span className='relative flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary'>
                <Radar className='size-7' />
                <span className='absolute inset-0 animate-ping rounded-2xl bg-primary/10' />
            </span>
            <div className='flex max-w-md flex-col gap-1.5'>
                <h2 className='text-lg font-semibold'>Collecting today&apos;s jobs</h2>
                <p className='text-sm leading-relaxed text-muted-foreground'>
                    We are reading the career pages of every company on TechJobs for the first time. This takes about a minute, and the page fills in on its own.
                </p>
            </div>
            <p className='flex items-center gap-2 text-sm font-medium text-primary'>
                <Loader2 className='size-4 animate-spin' />
                {data?.openJobs ? `${data.openJobs} jobs collected so far` : 'Starting up'}
            </p>
        </div>
    )
}
